import api from "./client";
import { enqueue as enqueueOffline } from "@/lib/offlineQueue";
import type { PaginatedResponse } from "@/types/common";
import type {
  PendingReviewRow,
  Project,
  TimeEntry,
  Timesheet,
  TimesheetPolicy,
  TimesheetStatus,
  WeeklyGridResponse,
} from "@/types/time";

// Network-error detection for the offline-queue fallback. Axios surfaces
// offline/DNS/connection refusals without `error.response`; anything with
// a response (including 4xx) is treated as a real server reply and is
// NOT queued — queueing a 401 would just loop.
function isOfflineError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (err && typeof err === "object" && "response" in err) {
    return (err as { response?: unknown }).response === undefined;
  }
  return true;
}

export async function clockIn(projectCode?: string, notes?: string) {
  try {
    const { data } = await api.post("/time/entries/clock-in", {
      project_code: projectCode,
      notes,
    });
    return data;
  } catch (err) {
    if (isOfflineError(err)) {
      await enqueueOffline({ kind: "clock-in", projectCode, notes });
      // Caller sees a "queued offline" marker; the shape matches a
      // partial TimeEntry enough for the UI to show optimistic state.
      return { queued_offline: true, queued_at: new Date().toISOString() };
    }
    throw err;
  }
}

export async function clockOut(entryId: string) {
  try {
    const { data } = await api.put(`/time/entries/${entryId}/clock-out`);
    return data;
  } catch (err) {
    if (isOfflineError(err)) {
      await enqueueOffline({ kind: "clock-out", entryId });
      return { queued_offline: true, queued_at: new Date().toISOString() };
    }
    throw err;
  }
}

export async function createManualEntry(
  clockInTime: string,
  clockOutTime: string,
  projectCode?: string,
  notes?: string,
) {
  const { data } = await api.post("/time/entries/manual", {
    clock_in: clockInTime,
    clock_out: clockOutTime,
    project_code: projectCode,
    notes,
  });
  return data;
}

export async function getActiveEntry(): Promise<TimeEntry | null> {
  const { data } = await api.get("/time/entries/active");
  return data;
}

export async function listTimeEntries(
  page = 1,
  perPage = 20,
  employeeId?: string,
): Promise<PaginatedResponse<TimeEntry>> {
  const params: Record<string, string | number> = { page, per_page: perPage };
  if (employeeId) params.employee_id = employeeId;
  const { data } = await api.get("/time/entries", { params });
  return data;
}

export async function listTimesheets(
  page = 1,
  perPage = 20,
  employeeId?: string,
  status?: TimesheetStatus,
): Promise<PaginatedResponse<Timesheet>> {
  const params: Record<string, string | number> = { page, per_page: perPage };
  if (employeeId) params.employee_id = employeeId;
  if (status) params.status = status;
  const { data } = await api.get("/time/timesheets", { params });
  return data;
}

export async function submitTimesheet(timesheetId: string, notes?: string) {
  const { data } = await api.put(`/time/timesheets/${timesheetId}/submit`, { notes });
  return data;
}

export async function reviewTimesheet(
  timesheetId: string,
  status: "APPROVED" | "REJECTED",
  notes?: string,
) {
  const { data } = await api.put(`/time/timesheets/${timesheetId}/review`, {
    status,
    notes,
  });
  return data;
}

// ── Weekly grid / copy-previous-week ──

export async function getWeeklyGrid(
  weekStart: string,
  employeeId?: string,
): Promise<WeeklyGridResponse> {
  const params: Record<string, string> = { week_start: weekStart };
  if (employeeId) params.employee_id = employeeId;
  const { data } = await api.get("/time/timesheets/weekly", { params });
  return data;
}

export async function copyPreviousWeek(
  weekStart: string,
): Promise<{ copied: number; week_start: string }> {
  const { data } = await api.post(
    "/time/timesheets/copy-previous-week",
    null,
    { params: { week_start: weekStart } },
  );
  return data;
}

// ── Pending reviews + CSV export ──

export async function listPendingReviews(): Promise<PendingReviewRow[]> {
  const { data } = await api.get("/time/pending-reviews");
  return data;
}

export async function exportTimeCsv(
  start: string,
  end: string,
  employeeId?: string,
  projectId?: string,
): Promise<Blob> {
  const params: Record<string, string> = { start, end };
  if (employeeId) params.employee_id = employeeId;
  if (projectId) params.project_id = projectId;
  const res = await api.get("/time/export.csv", {
    params,
    responseType: "blob",
  });
  return res.data as Blob;
}

// ── Projects registry ──

export async function listProjects(
  includeInactive = false,
): Promise<Project[]> {
  const { data } = await api.get("/time/projects", {
    params: { include_inactive: includeInactive },
  });
  return data;
}

export async function createProject(body: Partial<Project>): Promise<Project> {
  const { data } = await api.post("/time/projects", body);
  return data;
}

export async function updateProject(
  id: string,
  body: Partial<Project>,
): Promise<Project> {
  const { data } = await api.put(`/time/projects/${id}`, body);
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await api.delete(`/time/projects/${id}`);
}

// ── Timesheet policy ──

export async function getTimesheetPolicy(): Promise<TimesheetPolicy> {
  const { data } = await api.get("/time/policy");
  return data;
}

export async function upsertTimesheetPolicy(
  body: Partial<TimesheetPolicy>,
): Promise<TimesheetPolicy> {
  const { data } = await api.put("/time/policy", body);
  return data;
}
